/* eslint-disable no-param-reassign */
import { get, isNumber, isUndefined, pickBy } from 'lodash';
import moment from 'moment';
import { transformRequestQueryParams } from '@core/common/transformers/transform-request-query-params';
import {
  METRIC_NAMES, RESOURCES_CONFIGURATION_INTERVAL as INTERVAL,
  AGENT_STATUSES, USER_TYPES_NAMES,
  TIMEFRAME_FILTER_TYPES, GRAPH_RESOLUTION,
} from '@config/Consts';
import { convertTimeFrameToMinutes, makeArrayOfStrings } from '@core/utils/utils';
import CSATModel from '@core/models/CSATModel';
import AgentViewModel from '@core/models/AgentViewModel';
import BotManagerMetricsModel from '@core/models/BotManagerMetricsModel';
import BotManagerHistoricalModel from '@core/models/BotManagerHistoricalModel';
import BaseClient from '@core/common/BaseClient';
import { RESOURCES as REPORTING_RESOURCES } from '@core/clients/ReportingClient';
import { RESOURCES as AGENT_MANAGER_RESOURCES, METRICS_RESOURCE_PARAMS } from '@core/clients/AgentManagerClient';

const AGENT_VIEW_API_METRICS_CONSTS = {
  [METRIC_NAMES.AGENT_VIEW_ASSIGNED_CONVERSATIONS]: 'assigned_conversations',
  [METRIC_NAMES.AGENT_VIEW_AGENT_STATUS]: 'agent_current_status',
  [METRIC_NAMES.AGENT_VIEW_STATUS_REASON]: 'agent_current_status_with_reason',
  [METRIC_NAMES.AGENT_VIEW_TRANSFER_RATE]: 'transfer_rate',
  [METRIC_NAMES.AGENT_VIEW_CLOSED_RATE]: 'closed_rate',
  [METRIC_NAMES.AGENT_VIEW_CONCLUDED_CONVERSATIONS]: 'concluded_conversations',
  [METRIC_NAMES.AGENT_VIEW_PENDING_RATE]: 'pending_agent_response_rate',
  [METRIC_NAMES.AGENT_VIEW_INTENTS_MATCHED_RATE]: 'intent_matched_rate',
};

const DEFAULT_STATUSES_TO_RETRIEVE = [
  AGENT_STATUSES.ONLINE,
  AGENT_STATUSES.BACK_SOON,
  AGENT_STATUSES.AWAY,
];

// REQUEST TRANSFORMERS
function prepareBotsListRequest(settings, httpMethod, params, queryParams = {}) {
  return {
    method: httpMethod,
    interval: INTERVAL.GENERAL_RESOURCE, // TODO daniel: check if for graph we need longer interval
    body: params,
    config: {
      params: queryParams,
    },
  };
}

function transformMessagingCsatDistributionRequest(settings = {}) {
  const params = {};
  const { filter, timeframe, currentPageBotIds, botIds } = settings;
  const { bots: botsFilter } = filter;

  if (timeframe) {
    params.timeframe = convertTimeFrameToMinutes(timeframe);
  }
  if (currentPageBotIds && currentPageBotIds.length > 0) {
    params.agentIds = currentPageBotIds;
  } else if (botsFilter && botsFilter.length > 0) {
    params.agentIds = botsFilter.toString();
  } else if (botIds && botIds.length > 0) {
    params.agentIds = botIds;
  }
  return prepareBotsListRequest(settings, BaseClient.HTTP_METHODS.POST, params);
}

function transformAgentViewRequest(settings = {}) {
  const params = {
    filters: {
      userTypes: [USER_TYPES_NAMES.BOT],
      effectiveAgentStatus: DEFAULT_STATUSES_TO_RETRIEVE,
      time: {
        // setting values for the interceptor to fill
        from: -1,
        to: -1,
      },
    },
    includeAgentMetadata: true,
    metricsToRetrieveCurrentValue: [
      AGENT_VIEW_API_METRICS_CONSTS[METRIC_NAMES.AGENT_VIEW_ASSIGNED_CONVERSATIONS],
      AGENT_VIEW_API_METRICS_CONSTS[METRIC_NAMES.AGENT_VIEW_AGENT_STATUS],
      AGENT_VIEW_API_METRICS_CONSTS[METRIC_NAMES.AGENT_VIEW_STATUS_REASON],
      AGENT_VIEW_API_METRICS_CONSTS[METRIC_NAMES.AGENT_VIEW_PENDING_RATE],
    ],
    metricsToRetrieveByTime: [
      AGENT_VIEW_API_METRICS_CONSTS[METRIC_NAMES.AGENT_VIEW_TRANSFER_RATE],
      AGENT_VIEW_API_METRICS_CONSTS[METRIC_NAMES.AGENT_VIEW_CLOSED_RATE],
      AGENT_VIEW_API_METRICS_CONSTS[METRIC_NAMES.AGENT_VIEW_INTENTS_MATCHED_RATE],
      AGENT_VIEW_API_METRICS_CONSTS[METRIC_NAMES.AGENT_VIEW_CONCLUDED_CONVERSATIONS],
    ],
  };
  const queryParams = {};

  const { filter } = settings;
  const { status: statusFilter, bots: botsFilter,
    skills: skillsFilter, groupIds: groupsFilter } = filter;

  if (statusFilter && statusFilter.length) {
    params.filters.effectiveAgentStatus = statusFilter;
  }

  if (botsFilter && botsFilter.length) { // table filter
    params.filters.agentIds = botsFilter;
  }

  if (skillsFilter && skillsFilter.length) {
    params.filters.agentSkillIds = makeArrayOfStrings(skillsFilter);
  }

  if (groupsFilter && groupsFilter.length) {
    params.filters.agentGroupIds = groupsFilter;
  }
  return prepareBotsListRequest(
    settings,
    BaseClient.HTTP_METHODS.POST,
    params,
    transformRequestQueryParams(queryParams, settings),
  );
}

function transformMetricsRequest(settings) {
  const params = {
    filters: {
      userTypes: [USER_TYPES_NAMES.BOT],
      time: {
        // setting values for the interceptor to fill
        from: -1,
        to: -1,
      },
    },
    metricsToRetrieveCurrentValue: [
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_CURRENT_VALUE.ONLINE_AGENTS,
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_CURRENT_VALUE.BACK_SOON_AGENTS,
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_CURRENT_VALUE.AWAY_AGENTS,
    ],
  };

  const { filter } = settings;
  const { bots: botsFilter, skills: skillsFilter, groupIds: groupsFilter } = filter;

  if (botsFilter && botsFilter.length > 0) {
    params.filters.agentIds = botsFilter;
  }
  if (groupsFilter && groupsFilter.length > 0) {
    params.filters.agentGroupIds = makeArrayOfStrings(groupsFilter);
  }
  if (skillsFilter && skillsFilter.length > 0) {
    params.filters.skillIds = makeArrayOfStrings(skillsFilter);
  }

  return prepareBotsListRequest(settings, BaseClient.HTTP_METHODS.POST, params);
}

function transformHistoricalRequest(settings) {
  const params = {
    filters: {
      userTypes: [USER_TYPES_NAMES.BOT],
      time: {
        // setting values for the interceptor to fill
        from: -1,
        to: -1,
      },
    },
    metricsToRetrieveByTime: [
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_BY_TIME.CONCLUDED_CONVERSATIONS,
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_BY_TIME.TRANSFERS,
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_BY_TIME.CLOSED,
    ],
  };

  const { filter } = settings;
  const { bots: botsFilter,
    skills: skillsFilter, groupIds: groupsFilter } = filter;
  // const { status: statusFilter, bots: botsFilter,
  //   skills: skillsFilter, groupIds: groupsFilter } = filter;

  // if (statusFilter && statusFilter.length) {
  //   params.filters.effectiveAgentStatus = statusFilter;
  // }

  if (botsFilter && botsFilter.length) { // table filter
    params.filters.agentIds = botsFilter;
  }

  if (skillsFilter && skillsFilter.length) {
    params.filters.agentSkillIds = makeArrayOfStrings(skillsFilter);
  }

  if (groupsFilter && groupsFilter.length) {
    params.filters.agentGroupIds = groupsFilter;
  }

  const timeframeType = settings.timeframe.type;
  let duration = timeframeType === TIMEFRAME_FILTER_TYPES.RELATIVE ?
    settings.timeframe.relativeHour : moment().format('HH') - settings.timeframe.absoluteHour;
  duration = duration || 1;
  const interval = (60 * duration) / GRAPH_RESOLUTION;

  const queryParams = {
    // verbose: true, // this is for debug purpose only in BE
    interval,
  };

  return prepareBotsListRequest(settings, BaseClient.HTTP_METHODS.POST, params, queryParams);
}

// RESPONSE TRANSFORMERS /////////
function transformMessagingCsatDistributionResponse(response) {
  let data = null;
  const csatRecords = get(response, 'data.agentsMetrics.metricsPerAgent');
  if (!isUndefined(csatRecords)) {
    data = [];
    Object.keys(csatRecords).forEach((key) => {
      const csat = csatRecords[key].csat_score;
      const botRecord = {
        id: key,
        metrics: new CSATModel({
          [METRIC_NAMES.CSAT]: isNumber(csat) ? Math.round(csat * 100) : null,
        }),
      };
      data.push(botRecord);
    });
  }
  return data;
}

function transformMetricsResponse(response) {
  let data = null;
  const botStatusRecord = get(response, 'data.all');
  if (!isUndefined(botStatusRecord)) {
    data = new BotManagerMetricsModel({
      [METRIC_NAMES.AGENT_VIEW_METRICS_AWAY_AGENTS]: botStatusRecord.awayAgents || 0,
      [METRIC_NAMES.AGENT_VIEW_METRICS_BACK_SOON_AGENTS]: botStatusRecord.backSoonAgents || 0,
      [METRIC_NAMES.AGENT_VIEW_METRICS_ONLINE_AGENTS]: botStatusRecord.onlineAgents || 0,
    });
  }

  return data && pickBy(data, v => v !== undefined);
}

function transformHistoricalResponse(response) {
  let data = null;
  const historicalRecords = get(response, 'data.historicalMetricsIntervals');
  if (historicalRecords) {
    data = new BotManagerHistoricalModel({ historicalMetricsIntervals: historicalRecords });
  }
  return data;
}

function transformAgentViewResponse(response) {
  let data = null;
  const agentViewRecords = get(response, 'data.agentViewRecords');
  const convertToPercentages = (num = 0) => Math.round(num * 100);

  if (agentViewRecords) {
    data = [];
    agentViewRecords.forEach((record) => {
      const agentMetadata = record.agentMetadata || {};
      const agentRecord = {
        id: record.agentId,
        metrics: new AgentViewModel({
          [METRIC_NAMES.AGENT_VIEW_ASSIGNED_CONVERSATIONS]: record.assignedConversations || 0,
          [METRIC_NAMES.AGENT_VIEW_AGENT_STATUS]: record.agentCurrentStatus,
          [METRIC_NAMES.AGENT_VIEW_STATUS_REASON]: record.agentCurrentStatusReason,
          [METRIC_NAMES.AGENT_VIEW_AGENT_NAME]: agentMetadata.agentName,
          [METRIC_NAMES.AGENT_VIEW_AGENT_GROUP]: agentMetadata.agentGroupName,
          [METRIC_NAMES.AGENT_VIEW_AGENT_SKILLS]: agentMetadata.agentSkills,
          [METRIC_NAMES.AGENT_VIEW_AGENT_PICTURE]: agentMetadata.agentPictureUrl || '',
          [METRIC_NAMES.AGENT_VIEW_CONCLUDED_CONVERSATIONS]: record.concludedConversations || 0,
          [METRIC_NAMES.AGENT_VIEW_TRANSFER_RATE]: convertToPercentages(record.transferRate),
          [METRIC_NAMES.AGENT_VIEW_CLOSED_RATE]: convertToPercentages(record.closedRate),
          [METRIC_NAMES.AGENT_VIEW_PENDING_RATE]:
            convertToPercentages(record.pendingAgentResponseRate),
          [METRIC_NAMES.AGENT_VIEW_INTENTS_MATCHED_RATE]:
            convertToPercentages(record.intentMatchedRate),
        }),
      };
      data.push(agentRecord);
    });
  }
  const botsCount = get(response, 'data.metadata.count');
  return { botsCount, data };
}

const RESOURCE_TRANSFORM_REQUEST_METHOD_MAP = {
  [REPORTING_RESOURCES.MESSAGING_CSAT_DISTRIBUTIONS]: transformMessagingCsatDistributionRequest,
  [AGENT_MANAGER_RESOURCES.AGENT_VIEW]: transformAgentViewRequest,
  [AGENT_MANAGER_RESOURCES.METRICS]: transformMetricsRequest,
  [AGENT_MANAGER_RESOURCES.HISTORICAL]: transformHistoricalRequest,
};

const RESOURCE_TRANSFORM_RESPONSE_METHOD_MAP = {
  [REPORTING_RESOURCES.MESSAGING_CSAT_DISTRIBUTIONS]: transformMessagingCsatDistributionResponse,
  [AGENT_MANAGER_RESOURCES.AGENT_VIEW]: transformAgentViewResponse,
  [AGENT_MANAGER_RESOURCES.METRICS]: transformMetricsResponse,
  [AGENT_MANAGER_RESOURCES.HISTORICAL]: transformHistoricalResponse,
};

function transformRequest(resourceName, settings) {
  return RESOURCE_TRANSFORM_REQUEST_METHOD_MAP[resourceName](settings);
}

function transformResponse(resourceName, response) {
  return RESOURCE_TRANSFORM_RESPONSE_METHOD_MAP[resourceName](response);
}

export default {
  transformRequest,
  transformResponse,
  AGENT_VIEW_API_METRICS_CONSTS,
  DEFAULT_STATUSES_TO_RETRIEVE,
};
